import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, Phone, Lock, UserPlus, ShieldCheck, ArrowLeft, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRegister, apiVerifyOtp, apiResendOtp } from '../services/auth';
import useSeo from '../hooks/useSeo';

/**
 * Extract error details from ANY error shape.
 * Returns { message: string, errors: object | null, raw: any }
 */
function extractErrorDetails(error) {
  console.log('🔴 Raw error:', error); // For debugging

  // 1. If the error has a `data` property (custom HTTP wrapper)
  if (error?.data && typeof error.data === 'object') {
    const data = error.data;
    return {
      message: data.message || 'An error occurred.',
      errors: data.errors || null,
      raw: data,
    };
  }

  // 2. Axios error with response.data
  if (error?.response?.data) {
    const data = error.response.data;
    return {
      message: data.message || 'An error occurred.',
      errors: data.errors || null,
      raw: data,
    };
  }

  // 3. Plain object with message/errors directly
  if (error && typeof error === 'object' && error !== null) {
    if (error.message || error.errors) {
      return {
        message: error.message || 'An error occurred.',
        errors: error.errors || null,
        raw: error,
      };
    }
    // If it's an object but has no message/errors, stringify it
    return {
      message: JSON.stringify(error),
      errors: null,
      raw: error,
    };
  }

  // 4. Error instance
  if (error instanceof Error) {
    return { message: error.message, errors: null, raw: error };
  }

  // 5. String or primitive
  if (typeof error === 'string') {
    return { message: error, errors: null, raw: error };
  }

  // 6. Ultimate fallback
  return { message: 'An unexpected error occurred.', errors: null, raw: error };
}

export default function Register() {
  useSeo({
    title: 'Create account',
    description: 'Sign up for Maa Florist Mumbai and get updates on new resin pieces and exclusive offers.',
    canonical: '/register',
  });

  const loc = useLocation();
  const preVerifyEmail = loc.state?.verify_email;
  const preVerifyDevOtp = loc.state?.dev_otp;

  const [step, setStep] = useState(preVerifyEmail ? 'verify' : 'signup');
  const [form, setForm] = useState({
    name: '',
    email: preVerifyEmail || '',
    phone: '',
    password: '',
    password_confirmation: '',
  });
  const [otp, setOtp] = useState('');
  const [devOtp, setDevOtp] = useState(preVerifyDevOtp || '');
  const [info, setInfo] = useState(preVerifyEmail ? 'Please verify your email to continue.' : '');
  const [errorDetails, setErrorDetails] = useState(null);
  const [busy, setBusy] = useState(false);

  const { login } = useAuth();
  const nav = useNavigate();

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // ---- Signup ----
  const submitSignup = async (e) => {
    e.preventDefault();
    setErrorDetails(null);
    setInfo('');

    if (form.password !== form.password_confirmation) {
      setErrorDetails({ message: 'Passwords do not match', errors: null });
      return;
    }
    if (!/^[0-9]{10,15}$/.test(form.phone)) {
      setErrorDetails({ message: 'Enter a valid 10‑digit mobile number', errors: null });
      return;
    }

    setBusy(true);
    try {
      const res = await apiRegister(form);
      setInfo(res.message);
      if (res.dev_otp) setDevOtp(res.dev_otp);
      setStep('verify');
    } catch (error) {
      console.error('❌ Registration error:', error);
      setErrorDetails(extractErrorDetails(error));
    } finally {
      setBusy(false);
    }
  };

  // ---- Verify OTP ----
  const submitVerify = async (e) => {
    e.preventDefault();
    setErrorDetails(null);
    setInfo('');
    if (!/^\d{6}$/.test(otp)) {
      setErrorDetails({ message: 'Enter the 6‑digit code we emailed you', errors: null });
      return;
    }

    setBusy(true);
    try {
      const { user, token } = await apiVerifyOtp({ email: form.email, otp });
      login(user, token);
      nav('/account');
    } catch (error) {
      console.error('❌ Verification error:', error);
      setErrorDetails(extractErrorDetails(error));
    } finally {
      setBusy(false);
    }
  };

  // ---- Resend OTP ----
  const resend = async () => {
    setErrorDetails(null);
    setInfo('');
    setBusy(true);
    try {
      const res = await apiResendOtp({ email: form.email });
      setInfo(res.message);
      if (res.dev_otp) setDevOtp(res.dev_otp);
    } catch (error) {
      console.error('❌ Resend error:', error);
      setErrorDetails(extractErrorDetails(error));
    } finally {
      setBusy(false);
    }
  };

  // ---- Render error details ----
  const renderError = () => {
    if (!errorDetails) return null;

    const { message, errors, raw } = errorDetails;
    const hasFieldErrors = errors && Object.keys(errors).length > 0;

    return (
      <div className="mb-4 text-sm bg-red-50 border border-red-200 rounded-md px-3 py-2 text-red-700">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            {message && <div className="font-semibold">{message}</div>}

            {hasFieldErrors && (
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {Object.entries(errors).map(([field, msgs]) => (
                  <li key={field}>
                    <span className="capitalize font-medium">{field}</span>:{' '}
                    {Array.isArray(msgs) ? msgs.join(', ') : msgs}
                  </li>
                ))}
              </ul>
            )}

            {!message && !hasFieldErrors && (
              <details className="mt-1">
                <summary className="cursor-pointer text-red-600 font-medium">View full error details</summary>
                <pre className="mt-1 text-xs bg-red-100 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(raw, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center bg-[#f6f2ea] px-4 py-12">
      {/* --- Signup Form --- */}
      {step === 'signup' && (
        <form
          onSubmit={submitSignup}
          data-testid="register-form"
          className="bg-white w-full max-w-md rounded-xl shadow-sm border border-neutral-200 p-8"
        >
          <div className="text-center mb-6">
            <h1 className="font-serif text-3xl text-neutral-900">Create account</h1>
            <p className="text-sm text-neutral-600 mt-1">We&#39;ll email a 6‑digit code to verify.</p>
          </div>

          {renderError()}

          <label className="text-xs font-medium text-neutral-600">Full name *</label>
          <input
            required
            value={form.name}
            onChange={change('name')}
            data-testid="register-name"
            className="mt-1 mb-3 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
          />

          <label className="text-xs font-medium text-neutral-600 flex items-center gap-1">
            <Mail className="w-3 h-3" /> Email *
          </label>
          <input
            required
            type="email"
            value={form.email}
            onChange={change('email')}
            data-testid="register-email"
            className="mt-1 mb-3 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
          />

          <label className="text-xs font-medium text-neutral-600 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Mobile *
          </label>
          <input
            required
            type="tel"
            pattern="[0-9]{10,15}"
            value={form.phone}
            onChange={change('phone')}
            data-testid="register-phone"
            placeholder="10‑digit mobile"
            className="mt-1 mb-3 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
          />

          <label className="text-xs font-medium text-neutral-600 flex items-center gap-1">
            <Lock className="w-3 h-3" /> Password *
          </label>
          <input
            required
            type="password"
            minLength={6}
            value={form.password}
            onChange={change('password')}
            data-testid="register-password"
            className="mt-1 mb-3 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
          />

          <label className="text-xs font-medium text-neutral-600">Confirm password *</label>
          <input
            required
            type="password"
            minLength={6}
            value={form.password_confirmation}
            onChange={change('password_confirmation')}
            data-testid="register-password-confirmation"
            className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]"
          />

          <button
            disabled={busy}
            data-testid="register-submit"
            className="mt-6 w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-bold py-3 rounded-md tracking-wide transition-colors flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" /> {busy ? 'Sending code…' : 'Send verification code'}
          </button>

          <div className="mt-4 text-sm text-center text-neutral-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#1e3a2b] font-medium hover:underline">
              Log in
            </Link>
          </div>
        </form>
      )}

      {/* --- Verify OTP Form --- */}
      {step === 'verify' && (
        <form
          onSubmit={submitVerify}
          data-testid="verify-otp-form"
          className="bg-white w-full max-w-md rounded-xl shadow-sm border border-neutral-200 p-8"
        >
          {/* ✅ FIX: Updated Back Button logic */}
          <button
            type="button"
            onClick={() => {
              if (preVerifyEmail) {
                nav('/login'); // If they came from Login, send them back to Login
              } else {
                setStep('signup'); // If they came from Register, go back to Register
                setOtp(''); // Clear the OTP input when going back
              }
            }}
            className="text-sm text-neutral-500 hover:text-[#1e3a2b] inline-flex items-center gap-1 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#1e3a2b]/10 grid place-items-center">
              <ShieldCheck className="w-7 h-7 text-[#1e3a2b]" />
            </div>
            <h1 className="mt-4 font-serif text-3xl text-neutral-900">Verify your email</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Enter the 6‑digit code we sent to <b>{form.email}</b>.
            </p>
          </div>

          {info && (
            <div className="mb-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md px-3 py-2">
              {info}
            </div>
          )}
          {/* {devOtp && (
            <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">
              Dev mode: your code is <b className="tracking-widest">{devOtp}</b>. In production the code is emailed only.
            </div>
          )} */}
          {renderError()}

          <label className="text-xs font-medium text-neutral-600">6‑digit code</label>
          <input
            required
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            data-testid="verify-otp-input"
            className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-3 text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:border-[#1e3a2b]"
            placeholder="••••••"
          />

          <button
            disabled={busy}
            data-testid="verify-otp-submit"
            className="mt-6 w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-bold py-3 rounded-md tracking-wide transition-colors"
          >
            {busy ? 'Verifying…' : 'Verify & continue'}
          </button>

          <div className="mt-4 text-sm text-center text-neutral-600">
            Didn&#39;t receive the code?{' '}
            <button
              type="button"
              onClick={resend}
              disabled={busy}
              className="text-[#1e3a2b] font-medium hover:underline disabled:opacity-50"
            >
              Resend
            </button>
          </div>
        </form>
      )}
    </main>
  );
}